import React, { useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/apiClient";
import { useAccount } from "@/lib/AccountContext";
import { accountAuthErrorKey, authenticateAndContinue } from "@/lib/accountAuthFlow";
import { getAccountReturnIntent, getPendingClaim, safeReturnPath } from "@/lib/accountStorage";
import { validateAccountForm } from "@/lib/accountValidation";
import { EMPTY_PROFILE, profilePayload } from "@/lib/accountProfile";
import { AccountDemographicFields, AccountNameField } from "@/components/AccountProfileFields";
import { localizedPath, useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AccountAuth({ mode }) {
  const isRegister = mode === "register";
  const {
    user,
    login,
    register,
    claimResult,
    isAuthenticated,
    loading: accountLoading,
  } = useAccount();
  const { t, lang } = useLang();
  const { refresh } = useAuth();
  const [preToken, setPreToken] = useState(null);
  const [code, setCode] = useState("");
  const copy = (id, en) => lang === "id" ? id : en;
  const credentialError = copy("Email/username atau kata sandi tidak valid.", "Invalid email/username or password.");
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fallbackReturnTo = safeReturnPath(location.state?.returnTo);
  const pendingIntent = useMemo(() => getAccountReturnIntent(), []);
  const continuingSave = Boolean(
    pendingIntent?.claimCode && getPendingClaim(pendingIntent.claimCode),
  );

  useSEO({
    title: `${isRegister ? t("account.register") : t("account.signin")} — Mindovia`,
    description: t("account.auth.description"),
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  const shouldRedirectPublicAccount = isRegister || user?.status === "deleting";
  if (!accountLoading && isAuthenticated && !continuingSave && shouldRedirectPublicAccount) {
    return (
      <Navigate
        to={user?.status === "deleting" ? "/account/settings" : fallbackReturnTo}
        replace
      />
    );
  }

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const validationError = (isRegister || email.includes("@")) ? validateAccountForm({
      email,
      password,
      confirmPassword,
      mode,
    }) : (!email.trim() || !password ? "credentials_required" : null);
    if (validationError) {
      setError(isRegister ? t(`account.validation.${validationError}`) : credentialError);
      return;
    }

    setSubmitting(true);
    try {
      if (!isRegister && !email.includes("@")) {
        const { data } = await api.post("/auth/login", { username: email.trim(), password });
        setPassword("");
        if (data.requires_2fa) {
          setPreToken(data.pre_token);
          setCode("");
        } else {
          localStorage.setItem("se_token", data.token);
          await refresh();
          navigate("/admin", { replace: true });
        }
        return;
      }
      const continuation = await authenticateAndContinue({
        mode,
        login,
        register,
        claimResult,
        email,
        password,
        profile: isRegister ? profilePayload(profile) : undefined,
        fallbackReturnTo,
      });
      if (continuation.claimed) toast.success(t("account.save.saved"));
      if (continuation.errorKind) {
        toast.error(t(`account.claim.${continuation.errorKind}`));
      } else if (!continuation.claimed) {
        toast.success(t(isRegister ? "account.created" : "account.signed_in"));
      }
      navigate(continuation.returnTo, {
        replace: true,
        state: continuation.errorKind ? { accountClaimError: continuation.errorKind } : undefined,
      });
    } catch (requestError) {
      setError(isRegister ? t(accountAuthErrorKey(mode, requestError)) : (requestError?.response?.status === 429 ? copy("Terlalu banyak percobaan. Coba lagi nanti.", "Too many attempts. Try again later.") : credentialError));
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-2fa", { pre_token: preToken, code });
      localStorage.setItem("se_token", data.token);
      setPreToken(null);
      await refresh();
      navigate("/admin", { replace: true });
    } catch {
      setError(copy("Kode tidak valid atau sesi berakhir. Coba lagi atau masuk kembali.", "Invalid code or expired session. Try again or sign in again."));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="relative min-h-[72vh] overflow-hidden px-4 py-12 sm:px-6 sm:py-16" data-wrap="responsive">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-cream-100 via-background to-sage-50" />
      <div className={`relative mx-auto w-full ${isRegister ? "!max-w-xl" : "!max-w-md"} rounded-3xl border border-border bg-card/95 p-6 shadow-xl shadow-teal-900/5 sm:p-8`}>
        <Link to={localizedPath("/", lang)} className="mb-8 inline-flex items-center gap-2 text-sm text-teal-800">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-terracotta-500 text-cream-50"><Sparkles className="h-4 w-4" /></span>
          Mindovia
        </Link>
        <div className="mb-7">
          <h1 className="font-display text-3xl tracking-tight" data-testid="account-auth-title">
            {isRegister ? t("account.register") : copy("Masuk ke Mindovia", "Sign in to Mindovia")}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {continuingSave ? t("account.auth.continue_save") : isRegister ? t("account.auth.description") : copy("Gunakan email untuk akun pribadi, atau username untuk akun staff.", "Use your email for a personal account, or your username for a staff account.")}
          </p>
        </div>

        {preToken ? <form onSubmit={submitCode} className="space-y-5">
          <Label htmlFor="staff-code">{copy("Kode autentikator 6 digit", "6-digit authenticator code")}</Label>
          <Input id="staff-code" data-testid="admin-2fa-code" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          {error && <div role="alert">{error}</div>}
          <Button className="w-full" disabled={submitting || code.length !== 6} type="submit">{copy("Verifikasi", "Verify")}</Button>
          <button type="button" onClick={() => { setPreToken(null); setCode(""); setError(""); }}>{copy("Kembali", "Back")}</button>
        </form> : <form onSubmit={submit} className="space-y-5" noValidate>
          {isRegister && (
            <AccountNameField value={profile.name} onChange={(name) => setProfile({ ...profile, name })} t={t} />
          )}
          <div className="space-y-2">
            <Label htmlFor="account-email">{isRegister ? t("account.email") : copy("Email atau username", "Email or username")}</Label>
            <Input
              id="account-email"
              data-testid="account-email"
              type={isRegister ? "email" : "text"}
              autoComplete={isRegister ? "email" : "username"}
              inputMode={isRegister ? "email" : "text"}
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-describedby={error ? "account-auth-error" : undefined}
              className="h-12 bg-background"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-password">{t("account.password")}</Label>
            <Input
              id="account-password"
              data-testid="account-password"
              type="password"
              autoComplete={isRegister ? "new-password" : "current-password"}
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-describedby={error ? "account-auth-error" : "account-password-help"}
              className="h-12 bg-background"
            />
            {isRegister && <p id="account-password-help" className="text-xs text-muted-foreground">{t("account.password_help")}</p>}
            {!isRegister && (
              <div className="text-right">
                <Link
                  to="/account/forgot-password"
                  className="text-xs font-medium text-terracotta-700 underline-offset-4 hover:underline"
                  data-testid="account-forgot-password"
                >
                  {copy("Lupa kata sandi akun pribadi?", "Forgot your personal account password?")}
                </Link>
              </div>
            )}
          </div>
          {isRegister && (
            <div className="space-y-2">
              <Label htmlFor="account-confirm-password">{t("account.confirm_password")}</Label>
              <Input
                id="account-confirm-password"
                data-testid="account-confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                aria-describedby={error ? "account-auth-error" : undefined}
                className="h-12 bg-background"
              />
            </div>
          )}

          {isRegister && (
            <fieldset className="space-y-4 border-t border-border pt-5" data-testid="account-register-profile">
              <legend className="font-display text-lg text-teal-900">{t("account.profile.section")}</legend>
              <p className="text-xs leading-relaxed text-muted-foreground">{t("account.profile.demographics_help")}</p>
              <AccountDemographicFields profile={profile} onChange={setProfile} t={t} lang={lang} />
            </fieldset>
          )}

          {error && (
            <div id="account-auth-error" role="alert" aria-live="polite" className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="account-auth-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            className="h-12 w-full rounded-full bg-teal-800 text-cream-50 hover:bg-teal-700"
            data-testid="account-auth-submit"
          >
            {submitting ? t("account.working") : t(isRegister ? "account.register" : "account.signin")}
          </Button>
        </form>}

        <div className="mt-6 border-t border-border pt-5 text-center text-sm text-muted-foreground">
          {isRegister ? t("account.have_account") : copy("Belum punya akun pribadi?", "New to Mindovia?")} {" "}
          <Link
            to={isRegister ? "/login" : "/account/register"}
            state={{ returnTo: fallbackReturnTo }}
            className="font-medium text-terracotta-700 underline-offset-4 hover:underline"
          >
            {isRegister ? t("account.signin") : copy("Buat akun", "Create an account")}
          </Link>
        </div>
        <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sage-600" />
          {t("account.auth.optional")}
        </p>
      </div>
    </div>
  );
}
